export const en = {
  common: {
    loading: 'Loading...',
    initializing: 'Initializing...',
    continue: 'Continue',
    back: 'Back',
    retry: 'Retry',
    tryAgain: 'Try Again',
    goBack: 'Go Back',
    cancel: 'Cancel',
    close: 'Close',
    closeModal: 'Close modal',
    closeWindow: 'Close Window',
    startOver: 'Start Over',
    switchToMobile: 'Switch to Mobile',
    enableCamera: 'Enable Camera',
    returnToSite: 'Return to Site',
    notice: 'Notice',
    done: 'Done',
    saving: 'Saving...',
    processing: 'Processing...',
    error: 'Error',
    moreInfo: 'More information',
    productFallback: 'verification',
  },
  ageEstimateUnavailable: {
    title: 'We couldn’t check your age just now',
    description:
      'Something went wrong on our side while checking your selfie. You can try again, or verify with your ID using the selfie you just took.',
    descriptionIdOnly:
      'Something went wrong on our side while checking your selfie. You can continue by verifying with your ID, using the selfie you just took.',
    useId: 'Verify with ID',
  },
  pipelineRecovery: {
    title: 'Verification was temporarily interrupted',
    description:
      'We could not continue this step right now. Your verification is still active.',
    retrying: 'We are trying again automatically. Please keep this page open.',
    readyToRetry: 'Try again when you are ready.',
    retryingAction: 'Trying again…',
  },
  lifecycleRecovery: {
    loadTitle: 'We couldn’t load the next step',
    loadDescription: 'Check your connection and try again.',
    retryTitle: 'Let’s try that again',
    retryDescription: 'Something interrupted this step. Please try again.',
    retrySelfieTitle: 'Let’s take your selfie again',
    retrySelfieDescription: 'Face the camera in good light and try again.',
    retryIdTitle: 'Let’s take another photo of your ID',
    retryIdDescription: 'Make sure your whole ID is in view and easy to read.',
    restartStepTitle: 'Let’s try that step again',
    restartStepDescription: 'We need to start this step again to continue your verification.',
    startAgain: 'Start again',
    beginAgain: 'Begin again',
    cannotContinueTitle: 'This verification can’t continue',
    cannotContinueDescription: 'Begin again with a new verification.',
    waitingTitle: 'We’re finishing this step…',
    waitingDescription: 'Please keep this page open. We’ll continue automatically.',
    cancelledTitle: 'Verification cancelled',
    completeTitle: 'Verification complete',
    closeDescription: 'You can close this window.',
    returnDescription: 'You can return to the site.',
    continueTitle: 'Ready to continue',
    continueDescription: 'Continue to the next step.',
    rateLimitedTitle: 'We need a moment',
    rateLimitedDescription: 'We can’t check your next step right now. Your verification is still active.',
    checkStatus: 'Check status',
    finalizingTitle: 'Finalizing your verification…',
    finalizingDescription: 'Please keep this page open. This usually takes a few seconds.',
    unconfirmedTitle: 'We’re still confirming your result',
    unconfirmedDescription: 'This is taking longer than usual. You won’t need to repeat any steps.',
    checkAgain: 'Check again',
  },
  footer: {
    terms: 'Terms',
    privacy: 'Privacy',
    biometric: 'Biometric',
    languageLabel: 'Language',
    copyright: '© {{year}} {{companyName}}. All rights reserved.',
  },
  mainScreen: {
    title: 'Get verified anonymously',
    description:
      'Quick, private age verification. We never collect or share your details.',
    iconAlt: 'Secure verification',
    primaryAction: 'Verify Now',
    secondaryAction: 'Sign in with Emblem',
    separatorText: 'Already verified?',
    missingSession:
      'Session configuration is missing. Please start verification from the beginning.',
    directAccessBlocked:
      'Direct access is not allowed. Please start verification from the beginning.',
    emblemResumeNotice:
      "Emblem sign-in wasn't completed. You can continue with verification here.",
    emblemNoReusableNotice:
      "You don't have a reusable Emblem pass for this site yet. You can continue with verification here.",
  },
  emblemStepUp: {
    title: 'One more verification step',
    description:
      'Your Emblem passkey worked, but this site requires a higher level of verification.',
    primaryAction: 'Continue verification',
    iconAlt: 'Emblem verification step up',
    detailTitle: 'What happens next',
    detailPoint1: 'Complete a quick SafePassage verification for this site.',
    detailPoint2:
      'After you finish, your Emblem passkey will be upgraded for future L2 sites.',
    privacyHint: 'You are still verifying privately. Sites only receive the result.',
  },
  loading: {
    title: 'Setting up secure verification system',
    description: 'Your personal information is never collected, stored, or shared',
    status: {
      initializing: 'Initializing...',
      settingUpApiKey: 'Setting up API key...',
      ready: 'Ready to begin verification',
      errorRetrying: 'Error initializing - retrying...',
    },
    errors: {
      noApiKey: 'No API key configured. Please provide an API key.',
    },
  },
  cameraAccess: {
    title: 'Camera Access Required',
    description: 'Camera access is required to verify your age and continue',
    continueDesktop: 'Continue on Desktop',
    separatorText: 'OR',
    noWebcam: "Don't have a webcam?",
    privacyNotice: 'Your image is processed instantly and deleted immediately',
    qr: {
      title: 'Continue on Your Phone',
      description: 'Scan this code to complete verification on mobile',
      imageAlt: 'QR Code for mobile verification',
      instruction: "Point your phone's camera at this code",
      waiting: 'Waiting for connection...',
      generating: 'Generating QR code...',
      errorMissingSession: 'Missing session ID. Please go back and try again.',
      errorUnavailable: 'Unable to generate QR code. Please try again.',
      errorFailed: 'Failed to generate QR code. Please try again.',
    },
  },
  cameraSetup: {
    defaultMessage: 'Position your face in the circle',
    warmup: {
      preparing: 'Preparing your verification...',
      preparingDelayed:
        'Connecting to the verification service is taking longer than expected. Please keep this page open.',
      requesting: 'Waiting for camera permission...',
      initializing: 'Starting your camera...',
      positioning: 'Preparing your camera feed...',
      delayed:
        'This is taking longer than expected. Please keep this page open while your camera starts.',
    },
    analysis: {
      title: 'Processing your photo',
      description: 'This usually takes a few seconds.',
      privacy: 'Images are processed securely and not stored.',
      failureMessage: "We couldn't complete the check. Please try again.",
    },
    errors: {
      stalledTitle: 'Camera did not start',
      interruptedTitle: 'Camera was interrupted',
      missingSessionId: 'Missing session ID in verification result.',
      faceTimeout:
        'Face detection timed out. Please ensure your face is clearly visible in the camera.',
      faceTimeoutDetail:
        'No face was detected within the required time. Please try again and ensure your face is clearly visible in the circle.',
    },
  },
  cameraFeedback: {
    initial_position: 'Center your face in the frame',
    initial_lighting: 'Find good lighting',
    initial_stability: 'Keep your camera steady',
    wide_zone_position: 'Center your face in the frame',
    wide_zone_center: 'Center your face in the frame',
    wide_too_close: 'Move back slightly',
    wide_off_center: 'Center your face in the frame',
    wide_movement: 'Hold still',
    wide_gaze: 'Look straight ahead',
    wide_success: 'Great positioning!',
    zone_transition: 'Perfect! Now move a little closer',
    great_now_move_closer: 'Perfect! Now move a little closer',
    close_zone_position: 'Perfect! Hold right there',
    close_zone_gaze: 'Look straight ahead',
    close_too_far: 'Move a little closer',
    close_too_close: 'Move back slightly',
    close_off_center: 'Center your face in the frame',
    close_movement: 'Hold still',
    close_gaze: 'Look straight ahead',
    close_success: 'Perfect! Hold right there',
    processing: 'All done! Processing...',
    no_face_detected: 'No face detected',
    move_closer: 'Move a little closer',
    move_further: 'Move back slightly',
    center_face: 'Center your face in the frame',
    look_at_camera: 'Look straight ahead',
    improve_lighting: "Let's try that again",
    good_position: 'Looking good!',
    move_closer_to_camera: 'Move a little closer',
    move_further_from_camera: 'Move back slightly',
    good_wide_position: 'Great positioning!',
    good_close_position: 'Perfect! Hold right there',
  },
  cameraProcessing: {
    verifying: 'Great job! Processing your verification',
    analyzing: 'Analyzing...',
  },
  idRequest: {
    l2: {
      title: 'ID verification required',
      description:
        'Please provide a government-issued ID to complete verification',
    },
    l1: {
      failed: {
        title: "Our scan couldn't confirm your age",
        description: "We'll need a quick ID check to continue",
      },
      unavailable: {
        title: 'Let’s finish with a quick ID check',
        description:
          'We couldn’t check your age from your selfie this time, so we’ll use your ID instead.',
      },
      additional: {
        title: 'Additional verification needed',
        description:
          "To ensure compliance, we'll need to verify your age with an ID",
      },
    },
    primaryAction: 'Continue',
    privacyNotice: "We don't store or share your personal information",
    privacyTooltip:
      'Your ID is used only to verify your age. It is never saved, stored, or shared with the requesting website. The requesting website receives only a simple confirmation that you meet the age requirement.',
    iconAlt: 'ID card',
  },
  idCapture: {
    errorTitle: 'Photo Capture Failed',
    initializingTitle: 'Initializing camera',
    initializingDescription: 'Preparing document capture...',
    title: 'Take a photo of the front of your ID',
    recaptureTitle: 'We couldn’t accept that ID photo',
    instructions:
      'Make sure your ID is completely visible, with clear, readable text and all edges in frame',
    recaptureInstructions:
      'Make sure your ID is clear and fully visible, then retake the photo.',
    capturing: 'Capturing...',
    recaptureAction: 'Retake photo',
    captureAction: 'Scan ID',
    capturedTitle: 'Photo captured!',
    capturedDescription: 'Preparing for review...',
    cameraErrorTitle: 'Camera Error',
    cameraErrorAlt: 'Camera Error',
    cameraRecoveryDescription:
      'Try again, switch cameras if available, or check that this browser is allowed to use the camera.',
    cameraPermissionHelp:
      'On iPhone or iPad, check Safari camera access in Settings. In Chrome or Safari, use the camera icon in the address bar to allow camera access.',
    switchCameraAction: 'Switch camera',
  },
  idReview: {
    title: 'Review your scanned ID',
    description: 'Make sure all information is clearly visible',
    imageAlt: 'Captured ID',
    guidance: 'Your ID should be visible and readable.',
    missingImage: 'No image captured. Please go back and capture your ID.',
    submitAction: 'Continue',
    retakeAction: 'Retake',
  },
  idProcessing: {
    title: 'Verifying your ID',
    description:
      'This process usually takes about 20 to 30 seconds. In some cases, it may take up to 60 seconds.',
    keepOpen: 'Please keep this page open while verification completes.',
    info: 'Your document is processed securely and no personal information is stored.',
    status: {
      initial: 'Verification in progress.',
      stillWorking: 'Still verifying your document...',
      almostDone: 'Almost done. Thank you for your patience.',
    },
    steps: {
      received: 'Document received',
      analyzing: 'Analyzing information',
      verifying: 'Verifying authenticity',
      confirming: 'Confirming age',
    },
    errors: {
      noDocumentData: 'No document data found. Please capture your ID again.',
      noSessionId: 'No session ID available. Please restart the verification process.',
      selfieFetchFailed:
        'Failed to retrieve selfie from server. Please restart the verification process.',
      noApiKey:
        'API key not available. Please refresh and restart the verification process.',
      generic: 'Verification failed.',
    },
  },
  desktopWaiting: {
    title: 'Device Connected',
    description: 'Complete verification on your mobile device',
    iconAlt: 'Mobile Device',
    keepOpen: 'Keep this window open',
    redirectInfoLine1: "You'll automatically be redirected when",
    redirectInfoLine2: 'verification is complete.',
    expiredTitle: 'Session Expired',
    expiredDescription: 'Your verification session has expired. Please start over.',
  },
  returnToDesktop: {
    title: 'Return to your desktop',
    description:
      'Mobile verification complete. Return to your desktop browser to continue.',
    iconAlt: 'Desktop Computer',
  },
  recovery: {
    title: 'Continue Verification?',
    description: 'We found an in-progress verification session',
    lastSeen: 'Last seen: {{screen}}',
    progressLine: '{{age}} • {{progress}}',
    progress: {
      start: 'Just getting started',
      early: 'Early in the process',
      good: 'Making good progress',
      halfway: 'More than halfway done',
      almost: 'Almost finished',
      nearlyComplete: 'Nearly complete',
    },
    time: {
      minuteAgo: '{{count}} minute ago',
      minutesAgo: '{{count}} minutes ago',
      secondAgo: '{{count}} second ago',
      secondsAgo: '{{count}} seconds ago',
    },
    screens: {
      main: 'Welcome',
      emblemStepUp: 'Emblem Step Up',
      cameraSetup: 'Camera Setup',
      verification: 'Age Verification',
      idRequest: 'ID Required',
      idCapture: 'Document Capture',
      idProcessing: 'Processing Document',
      idReview: 'Review Capture',
      success: 'Verification Complete',
      failure: 'Verification Failed',
      error: 'Technical Error',
      unknown: '{{screenName}}',
    },
    actions: {
      continue: 'Continue Where I Left Off',
      startFresh: 'Start Fresh',
    },
    capabilities: {
      connection: '✓ Connection can be restored',
      camera: '✓ Camera settings saved',
      manual: 'Some settings may need to be reconfigured',
    },
    info: {
      continuingLabel: 'Continuing:',
      continuingDescription: 'Resume verification from where you left off.',
      startFreshLabel: 'Starting fresh:',
      startFreshDescription: 'Begin a new verification session.',
    },
  },
  errorScreen: {
    helpTitle: 'What you can do:',
    invalidSession: {
      title: 'Invalid Session',
      description:
        'The verification session is invalid or has expired. Please try starting the verification process again.',
      primaryAction: 'Contact Support',
      suggestion1: "Return to the merchant's website",
      suggestion2: 'Start the verification process again',
      suggestion3: 'Contact support if the problem persists',
    },
    expiredSession: {
      title: 'Session Expired',
      description:
        'Your verification session has expired. Return to the site or app where you started to begin again.',
      primaryAction: 'Return to Site',
      suggestion1: 'Return to the site or app where you started',
      suggestion2: 'Begin a new verification there',
      suggestion3: 'Contact the site for help if needed',
    },
    network: {
      title: 'Connection Problem',
      description:
        'Unable to connect to verification services. Please check your internet connection and try again.',
      suggestion1: 'Check your internet connection',
      suggestion2: "Disable VPN if you're using one",
      suggestion3: 'Try refreshing the page',
      suggestion4: 'Contact support if issues persist',
    },
    unsupported: {
      title: 'Browser Not Supported',
      description:
        "Your browser doesn't support the required features for age verification. Please use a modern browser like Chrome, Firefox, or Safari.",
      primaryAction: 'Download Chrome',
      suggestion1: 'Update your current browser',
      suggestion2: 'Try using Chrome, Firefox, or Safari',
      suggestion3: 'Enable JavaScript if disabled',
    },
    cameraDenied: {
      title: 'Camera Access Required',
      description:
        'Age verification requires camera access. Please allow camera permissions and try again.',
      suggestion1: 'Click the camera icon in your address bar',
      suggestion2: 'Select "Allow" for camera access',
      suggestion3: 'Refresh the page after granting permission',
    },
    missingReturnUrl: {
      title: 'Configuration Error',
      description:
        'The verification session is missing required configuration. Please contact the merchant to fix their integration.',
      primaryAction: 'Contact Merchant',
      suggestion1: "Return to the merchant's website",
      suggestion2: "Contact the merchant's support team",
      suggestion3: 'Report that their {{productName}} integration needs to be updated',
      suggestion4:
        'The merchant needs to provide a valid return URL in their SDK configuration',
    },
    directAccess: {
      title: 'Direct Access Not Allowed',
      description:
        'Age verification must be accessed through a merchant website using the {{productName}} SDK. You cannot access this service directly.',
      primaryAction: 'Close Page',
      iconAlt: 'Access blocked',
      suggestion1: "Go back to the merchant's website where you started",
      suggestion2:
        'Complete the age verification through their checkout or sign-up process',
      suggestion3: 'Do not bookmark or share this verification URL',
      suggestion4:
        'Each verification session is unique and must be initiated by the merchant',
    },
    subscriptionRequired: {
      title: 'Subscription Required',
      description:
        'This account needs an active subscription to run production verifications. Contact your administrator to upgrade the plan.',
      primaryAction: 'Contact Admin',
      portalAction: 'Open Billing Portal',
    },
    planLimitReached: {
      title: 'Monthly Limit Reached',
      description:
        'This account has reached its monthly verification limit. Contact your administrator to upgrade the plan or wait for the next billing period.',
      primaryAction: 'Contact Admin',
      portalAction: 'Open Billing Portal',
    },
    sandboxLimitReached: {
      title: 'Sandbox Limit Reached',
      description:
        'This account has reached its demo/test verification limit. A paid subscription is required to continue with production verifications.',
      primaryAction: 'Contact Admin',
      portalAction: 'Open Billing Portal',
    },
  },
  failure: {
    genericError: 'Verification failed',
    iconAlt: 'Verification unsuccessful',
    document: {
      title: 'Verification unsuccessful',
      description: "We couldn't complete your verification. Let's give it another try.",
      reason1: 'Document image quality issues',
      reason2: 'Information not clearly visible',
      reason3: 'Document type not supported',
      reason4: 'Age verification requirements not met',
    },
    face: {
      title: 'Verification unsuccessful',
      description: "We couldn't complete your verification. Let's give it another try.",
      reason1: 'Liveness check did not pass',
      reason2: 'Poor lighting conditions',
      reason3: 'Camera positioning issues',
      reason4: 'Movement during scanning',
      reason5: 'Face not clearly visible',
    },
    crossDeviceRestart: {
      title: 'Return to your desktop',
      description:
        'This verification must be restarted from the desktop browser that showed the QR code. Return there and click Try Again, then scan the new QR code with your phone.',
    },
    errors: {
      restartFailed:
        'Failed to restart verification. Please refresh the page and try again.',
      switchFailed: 'Failed to switch to mobile. Please refresh and try again.',
    },
  },
  success: {
    title: 'Age verification successful',
    emblemTitle: 'Emblem Account Created!',
    emblemDescription:
      'Your portable age verification is now ready. You can use your Emblem account to verify your age on any website that supports Emblem.',
    iconAlt: 'Success',
    continueAction: 'Continue to Site',
    separatorText: 'skip future verifications',
    createEmblemAction: 'Create Emblem Account',
    emblemEnrollmentComplete: 'Emblem account created',
    readyDescription: 'You can now access your content',
    redirectingIn: 'Redirecting in {{seconds}} seconds...',
    returnUrlInvalid:
      'Return URL is not configured or invalid. Please click Continue to proceed.',
    errors: {
      missingReturnUrl: 'Missing return URL - merchant SDK misconfiguration',
      emblemSignupFailed: 'Failed to create Emblem account',
    },
  },
  emblem: {
    info: {
      ariaLabel: 'Learn more about Emblem',
      trigger: "What's Emblem?",
      title: 'What is Emblem?',
      paragraph1:
        "Emblem securely saves your age verification, letting you prove you're 18+ without sharing personal details.",
      paragraph2:
        'After verifying your age through {{productName}}, you can create an Emblem account to skip future verifications across participating sites. Your verification stays private; sites only learn you are verified, nothing else.',
      linkLabel: 'Emblem Website',
    },
    signupInfo: {
      ariaLabel: 'Learn more about creating an Emblem account',
      trigger: 'Why create an account?',
      title: 'Create an Emblem Account',
      intro: 'Save your verification for future use! With an Emblem account, you can:',
      bullet1: 'Skip future age verifications on participating sites',
      bullet2: 'Keep your personal information private',
      bullet3: 'Access age-restricted content instantly',
      outro:
        "Your verification is stored securely. Sites only learn that you're verified, nothing else about you.",
      linkLabel: 'Emblem Website',
    },
  },
  mobileCompletion: {
    title: 'Verification Complete',
    description:
      'Mobile verification completed successfully. You can close this window and return to your desktop.',
    iconAlt: 'Desktop Computer',
    closeWindowFallback:
      'Please close this tab/window manually to return to your desktop.',
  },
  cameraErrorBoundary: {
    iconAlt: 'Camera Error',
    trySteps: 'Try these steps:',
    skipCamera: 'Continue Without Camera',
    permission: {
      title: 'Camera access required',
      description: 'To verify your age, we need access to your camera.',
      step1: "Click the camera icon in your browser's address bar",
      step2: 'Select "Allow" for camera access',
      step3: 'Click "Try Again" below',
    },
    notFound: {
      title: 'No camera detected',
      description: "We couldn't find a camera on your device.",
      step1: 'Check if your camera is properly connected',
      step2: 'Try using a different browser',
      step3: 'Use a device with a built-in camera',
    },
    inUse: {
      title: 'Camera in use',
      description: 'Your camera is being used by another application.',
      step1: 'Close other apps that might be using your camera',
      step2: 'Check for video calls or recording software',
      step3: 'Restart your browser and try again',
    },
    unsupported: {
      title: 'Browser not supported',
      description: "Your browser doesn't support camera access.",
      step1: 'Update to the latest version of your browser',
      step2: 'Try using Chrome, Firefox, Safari, or Edge',
      step3: 'Enable WebRTC in your browser settings',
    },
    default: {
      title: 'Camera error',
      description: 'We encountered an issue accessing your camera.',
      step1: 'Check your camera connection',
      step2: 'Refresh the page and try again',
      step3: 'Try using a different browser',
    },
    platform: {
      chrome: 'Chrome/Edge:',
      chromeInstruction: 'Click the camera icon in the address bar',
      firefox: 'Firefox:',
      firefoxInstruction: 'Click the permissions icon next to the URL',
      safari: 'Safari:',
      safariInstruction: 'Go to Safari -> Settings -> Websites -> Camera',
    },
  },
  verificationErrorBoundary: {
    iconAlt: 'Error',
    retryAttempt: 'Retry attempt {{attempt}} of {{max}}',
    errorDetails: 'Error Details',
    errorType: 'Error Type:',
    errorTypeGeneral: 'general',
    componentStack: 'Component Stack:',
    cameraDenied: {
      title: 'Camera access denied',
      description:
        'Please allow camera access in your browser settings to continue with verification.',
    },
    noCamera: {
      title: 'No camera found',
      description: 'Please ensure you have a working camera connected to your device.',
    },
    cameraError: {
      title: 'Camera error',
      description:
        'We encountered an issue with your camera. Please check your camera and try again.',
    },
    documentFailed: {
      title: 'Document capture failed',
      description:
        'We had trouble capturing your document. Please ensure good lighting and try again.',
    },
    connectionLost: {
      title: 'Connection lost',
      description:
        'We lost connection to our servers. Please check your internet connection and try again.',
    },
    multipleErrors: {
      title: 'Multiple errors occurred',
      description:
        "We're having persistent issues. Please try again later or contact support if the problem continues.",
    },
    default: {
      title: 'Something went wrong',
      description:
        'We encountered an unexpected error. Please try again or start over if the problem persists.',
    },
  },
  errorState: {
    title: 'Something went wrong',
  },
  notifications: {
    errorTitle: 'Error',
    successTitle: 'Success',
    infoTitle: 'Info',
    cameraAccessDenied:
      'Camera access was denied. Please grant camera permissions and try again.',
    noCameraFound: 'No camera found. Please ensure your device has a camera.',
    cameraInUse: 'Camera is already in use by another application.',
    networkError: 'Network error. Please check your connection and try again.',
    unexpectedError: 'An unexpected error occurred. Please try again.',
  },
  cameraErrors: {
    notAllowed:
      'We need camera access to continue. Select "Try Again" to request it. If your browser does not ask again, allow camera access for this site in your browser settings, then try again or reload the page.',
    notAllowedAction: 'Please click allow when prompted for camera access',
    security:
      'Camera access is blocked by your browser or device security settings. Allow camera access for this site in those settings, then try again or reload the page.',
    securityAction:
      'Allow camera access in your browser or device security settings',
    notFound: 'No camera found. Please ensure a camera is connected.',
    notFoundAction: 'Please connect a camera device',
    notReadable: 'Camera is already in use by another application.',
    notReadableAction: 'Please close other applications using the camera',
    overconstrained: 'Camera settings not supported. Trying with default settings...',
    overconstrainedAction: 'Will retry with basic camera settings',
    timeout: 'Camera initialization timeout. Please try again.',
    timeoutAction: 'Please try again',
    videoStalled:
      'Your camera turned on, but no video arrived. Select "Try Again" to restart the camera. If this keeps happening, close other apps that use the camera or reload the page.',
    videoStalledAction: 'Please try again to restart the camera',
    interrupted:
      'Your camera stopped unexpectedly, possibly because a call or another app used it. Select "Try Again" to restart the camera.',
    interruptedAction: 'Please try again to restart the camera',
    initializationFailed: 'Camera initialization failed. Please refresh and try again.',
    streamUnavailable: 'Camera stream unavailable. Please check your camera connection.',
    generic: 'Camera access failed. Please check your camera and try again.',
    genericAction: 'Please refresh the page and try again',
  },
  brandHeader: {
    tagline: 'Age Verification',
    merchantLabel: 'Verifying for',
    secureLabel: 'Secure',
  },
  faceMatchRecovery: {
    selfieRetry: {
      title: 'Let’s try another selfie',
      message:
        'We couldn’t match your selfie to your ID photo this time. ID photos can be years old, and changes in appearance or lighting can make it harder to recognize you.',
      tipsHeading: 'Try these tips for your next selfie:',
      tips: {
        straightOn: 'Face the camera straight on and keep your whole face in view.',
        lighting: 'Find a well-lit spot with the light in front of you.',
        expression:
          'Try the same expression as your ID photo. If you aren’t smiling in the photo, keep a relaxed, neutral expression.',
        eyes: 'Make sure we can see your eyes clearly, without glare or sunglasses.',
      },
      primaryAction: 'Take a new selfie',
      captureHeading: 'For your new selfie',
      captureTips: {
        straightOn: 'Face the camera straight on',
        lighting: 'Light in front of you',
        expression: 'Same expression as your ID photo',
        eyes: 'Eyes clearly visible, no sunglasses',
      },
      comparing: 'Comparing your new selfie with your ID…',
    },
    repeated: {
      title: 'Still having trouble? Let’s try again.',
      message:
        'An older ID photo or different lighting can make this harder. Face the camera in good light and try the same expression as your ID photo.',
      primaryAction: 'Take another selfie',
    },
    help: {
      heading: 'Need help?',
      differentIdText:
        'If you have another photo ID with a more recent picture of you, you can use it instead.',
      differentIdAction: 'Use a different ID',
    },
    differentId: {
      captureTitle: 'Take a photo of your other ID',
      captureInstructions:
        'Take a photo of the front of the other ID. Make sure it’s clear and fully visible.',
    },
    retainedIdUnavailable:
      'We need a new photo of your ID to finish. Make sure it’s clear and fully visible.',
  },
} as const;
